/**
 * Écran « Exercice comparé ».
 *
 * La grille de construction budgétaire montre **un scénario sur cinq ans**.
 * Celui-ci fait l'inverse : **un exercice, tous les scénarios**, poste par
 * poste. C'est la vue qu'on ouvre pour arbitrer une année — on n'y compare pas
 * des trajectoires mais des budgets, ligne à ligne, et l'écart entre eux se lit
 * directement.
 *
 * Aucun montant n'est calculé ici : tout vient du moteur.
 */

import { entier, euros, eurosSigne, keuros } from '@scc/core';
import type { Charge } from '../api.js';
import { h, vider } from '../dom.js';
import { TEINTES, graphiqueEmpile, legende, teinte } from '../graphiques.js';
import { badge, enteteSection, insight, kpi, noteLecture, regleAccent } from '../composants.js';

/** Nom court d'un scénario : ce qui précède le tiret cadratin. */
function court(libelle: string): string {
  return (libelle.split('—')[0] ?? libelle).trim();
}

/** Teinte d'un scénario, stable d'un écran à l'autre. */
function teinteScenario(cle: string, rang: number): string {
  if (cle === 'sc1') return TEINTES.slate;
  if (cle === 'sc2') return TEINTES.green;
  if (cle === 'sc3') return TEINTES.amber;
  if (cle === 'sim') return TEINTES.blue;
  return teinte(rang + 4);
}

/**
 * Exercice demandé depuis un autre écran.
 *
 * Les vues sont construites une fois puis masquées : un lien qui ouvre cet
 * écran sur 2029 doit pouvoir le dire, que la page existe déjà ou non. La
 * demande est donc mémorisée, et honorée à la construction comme à l'affichage.
 */
let exerciceDemande: number | null = null;
let appliquerDemande: ((annee: number) => void) | null = null;

/** Ouvre l'écran sur un exercice donné. Appelé avant la navigation. */
export function demanderExercice(annee: number): void {
  exerciceDemande = annee;
  appliquerDemande?.(annee);
}

export function vueExerciceCompare(d: Charge, allerA: (page: string) => void): HTMLElement {
  const annees = d.trajectoires[0]?.annees.map((a) => a.annee) ?? [];
  let anneeChoisie =
    exerciceDemande !== null && annees.includes(exerciceDemande)
      ? exerciceDemande
      : (annees[0] ?? d.meta.exerciceCourant);

  const zone = h('div', {});
  const selecteur = h('div', { classe: 'barre-actions' });

  const dessiner = (): void => {
    vider(zone);

    // Ce que chaque scénario dit de cet exercice.
    const colonnes = d.budgets
      .map((b, i) => {
        const ex = b.exercices.find((x) => x.exercice === anneeChoisie);
        const traj = d.trajectoires.find((t) => t.cle === b.scenario);
        const annee = traj?.annees.find((a) => a.annee === anneeChoisie);
        const passage = d.tresorerie.parScenario
          .find((x) => x.cle === b.scenario)
          ?.passage.find((p) => p.exercice === anneeChoisie);
        const pm = d.pointsMorts
          .find((x) => x.cle === b.scenario)
          ?.points.find((p) => p.exercice === anneeChoisie);
        const cellule = d.veille.lignes
          .find((l) => l.exercice === anneeChoisie)
          ?.cellules.find((c) => c.scenario === b.scenario);
        return { cle: b.scenario, libelle: b.libelle, couleur: teinteScenario(b.scenario, i), ex, annee, passage, pm, cellule };
      })
      .filter((c): c is typeof c & { ex: NonNullable<typeof c.ex> } => c.ex !== undefined);

    if (colonnes.length === 0) {
      zone.appendChild(h('p', { classe: 'note-lecture' }, 'Aucun scénario ne couvre cet exercice.'));
      return;
    }

    const seuil = d.seuils.applicables[anneeChoisie];
    // La nomenclature est la même pour tous les scénarios : le premier sert de
    // référence pour l'ordre des lignes et le regroupement par catégorie.
    const premiere = colonnes[0];
    if (!premiere) return;

    // ── Bandeau de tête : l'écart entre scénarios, en une ligne ───────────
    const resultats = colonnes.map((c) => c.ex.resultat);
    const reserves = colonnes.map((c) => c.annee?.reserve ?? 0);
    const meilleur = colonnes.reduce((a, b) => (b.ex.resultat > a.ex.resultat ? b : a));
    const pire = colonnes.reduce((a, b) => (b.ex.resultat < a.ex.resultat ? b : a));

    zone.appendChild(
      h(
        'div',
        { classe: 'kpi-grid' },
        kpi(
          'neutral',
          keuros(Math.max(...resultats) - Math.min(...resultats)),
          null,
          `Écart entre scénarios sur le résultat ${anneeChoisie}`,
          { sens: 'neutre', texte: `de ${keuros(Math.min(...resultats))} à ${keuros(Math.max(...resultats))}` },
        ),
        // Le libellé complet passerait à la ligne dans la police d'affichage :
        // le chiffre-clé porte le nom court, la mention porte le nom entier.
        kpi('progress', court(meilleur.libelle), null, 'Scénario le plus favorable', {
          sens: 'up',
          texte: `${meilleur.libelle} · résultat ${eurosSigne(meilleur.ex.resultat)}`,
        }),
        kpi('warning', court(pire.libelle), null, 'Scénario le moins favorable', {
          sens: 'down',
          texte: `${pire.libelle} · résultat ${eurosSigne(pire.ex.resultat)}`,
        }),
        kpi(
          (d.veille.lignes.find((l) => l.exercice === anneeChoisie)?.nbSousSeuil ?? 0) > 0 ? 'alert' : 'neutral',
          keuros(seuil?.seuil ?? 0),
          null,
          `Seuil d’alerte ${anneeChoisie}`,
          {
            sens: 'neutre',
            texte:
              (d.veille.lignes.find((l) => l.exercice === anneeChoisie)?.nbSousSeuil ?? 0) > 0
                ? `${entier(d.veille.lignes.find((l) => l.exercice === anneeChoisie)?.nbSousSeuil ?? 0)} scénario(s) en dessous`
                : 'aucun scénario en dessous',
          },
        ),
      ),
    );

    // ── La grille poste par poste ─────────────────────────────────────────
    const categoriesDe = (nature: 'produit' | 'charge'): string[] => [
      ...new Set(
        premiere.ex[nature === 'produit' ? 'produits' : 'charges'].map((l) => l.poste.categorie),
      ),
    ];

    /** Montant d'un poste dans un scénario donné, pour cet exercice. */
    const montant = (c: (typeof colonnes)[number], code: string): number =>
      [...c.ex.produits, ...c.ex.charges].find((l) => l.poste.code === code)?.montant ?? 0;

    /** Vrai quand le poste a été arbitré à la main dans ce scénario. */
    const arbitre = (c: (typeof colonnes)[number], code: string): boolean =>
      [...c.ex.produits, ...c.ex.charges].find((l) => l.poste.code === code)?.origine !== 'engendre';

    const corps: HTMLElement[] = [];
    for (const nature of ['produit', 'charge'] as const) {
      corps.push(
        h(
          'tr',
          { classe: 'groupe' },
          h('th', { colspan: colonnes.length + 2 }, nature === 'produit' ? 'Produits' : 'Charges'),
        ),
      );
      for (const cat of categoriesDe(nature)) {
        const postes = premiere.ex[nature === 'produit' ? 'produits' : 'charges'].filter(
          (l) => l.poste.categorie === cat,
        );

        // Sous-total de catégorie : la somme des postes qu'elle contient.
        corps.push(
          h(
            'tr',
            { classe: 'categorie' },
            h('th', { scope: 'row' }, cat),
            ...colonnes.map((c) =>
              h('td', {}, euros(postes.reduce((s, p) => s + montant(c, p.poste.code), 0))),
            ),
            h('td', {}, ''),
          ),
        );

        for (const p of postes) {
          const valeurs = colonnes.map((c) => montant(c, p.poste.code));
          const ecart = Math.max(...valeurs) - Math.min(...valeurs);
          corps.push(
            h(
              'tr',
              {},
              h('th', { scope: 'row' }, p.poste.libelle),
              ...colonnes.map((c, i) =>
                h(
                  'td',
                  { classe: arbitre(c, p.poste.code) ? 'saisi' : '' },
                  euros(valeurs[i] ?? 0),
                  arbitre(c, p.poste.code)
                    ? h('span', { classe: 'marque-saisi', title: 'montant arbitré' }, '·')
                    : null,
                ),
              ),
              // La colonne d'écart dit d'un coup d'œil où les scénarios divergent.
              h('td', { classe: ecart > 0 ? 'ecart' : '' }, ecart > 0 ? euros(ecart) : '—'),
            ),
          );
        }
      }
      corps.push(
        h(
          'tr',
          { classe: 'total' },
          h('th', { scope: 'row' }, nature === 'produit' ? 'Total des produits' : 'Total des charges'),
          ...colonnes.map((c) =>
            h('td', {}, euros(nature === 'produit' ? c.ex.totalProduits : c.ex.totalCharges)),
          ),
          h('td', {}, ''),
        ),
      );
    }

    corps.push(
      h(
        'tr',
        { classe: 'resultat' },
        h('th', { scope: 'row' }, 'Résultat de l’exercice'),
        ...colonnes.map((c) => h('td', {}, eurosSigne(c.ex.resultat))),
        h('td', {}, euros(Math.max(...resultats) - Math.min(...resultats))),
      ),
    );

    zone.appendChild(
      h(
        'div',
        { classe: 'enveloppe-table' },
        h(
          'table',
          { classe: 'table-budget' },
          h('caption', { classe: 'invisible' }, `Budgets comparés de l'exercice ${anneeChoisie}`),
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { scope: 'col' }, `Poste — ${anneeChoisie}`),
              ...colonnes.map((c) => h('th', { scope: 'col' }, c.libelle)),
              h('th', { scope: 'col' }, 'Écart'),
            ),
          ),
          h('tbody', {}, ...corps),
        ),
      ),
    );

    // ── Ce que l'exercice produit au-delà du budget ───────────────────────
    const rangee = (
      libelle: string,
      valeur: (c: (typeof colonnes)[number]) => string | Node,
      classe = '',
    ): HTMLElement =>
      h('tr', { classe }, h('th', { scope: 'row' }, libelle), ...colonnes.map((c) => h('td', {}, valeur(c))));

    zone.appendChild(
      h(
        'div',
        { classe: 'enveloppe-table' },
        h(
          'table',
          { classe: 'table-budget' },
          h('caption', { classe: 'invisible' }, `Conséquences de l'exercice ${anneeChoisie}`),
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { scope: 'col' }, 'Conséquence'),
              ...colonnes.map((c) => h('th', { scope: 'col' }, c.libelle)),
            ),
          ),
          h(
            'tbody',
            {},
            h('tr', { classe: 'groupe' }, h('th', { colspan: colonnes.length + 1 }, 'Inducteurs')),
            rangee('Adhérents payeurs', (c) => entier(c.ex.inducteurs.adherents)),
            rangee('Produit moyen par adhérent', (c) => euros(c.ex.inducteurs.produitMoyen)),
            rangee('Masse salariale chargée', (c) =>
              euros(c.ex.inducteurs.salairesBruts + c.ex.inducteurs.chargesPatronales),
            ),
            h('tr', { classe: 'groupe' }, h('th', { colspan: colonnes.length + 1 }, 'Solidité')),
            rangee('Réserve à la clôture', (c) => euros(c.annee?.reserve ?? 0)),
            rangee('Seuil d’alerte', () => euros(seuil?.seuil ?? 0)),
            rangee('Marge au seuil', (c) => eurosSigne(c.cellule?.marge ?? 0)),
            rangee(
              'Situation',
              (c) =>
                badge(
                  c.cellule?.sousSeuil === true ? 'alerte' : c.cellule?.serre === true ? 'vigilance' : 'favorable',
                  c.cellule?.sousSeuil === true
                    ? 'Sous le seuil'
                    : c.cellule?.serre === true
                      ? 'Marge serrée'
                      : 'Tenable',
                ),
            ),
            h('tr', { classe: 'groupe' }, h('th', { colspan: colonnes.length + 1 }, 'Trésorerie')),
            rangee('Trésorerie de clôture', (c) => euros(c.passage?.tresorerieCloture ?? 0)),
            rangee('Variation de l’exercice', (c) => eurosSigne(c.passage?.variationTresorerie ?? 0)),
            rangee('dont investissements', (c) => euros(c.passage?.investissements ?? 0)),
            h('tr', { classe: 'groupe' }, h('th', { colspan: colonnes.length + 1 }, 'Point mort')),
            rangee('Adhérents à l’équilibre', (c) => entier(c.pm?.adherents ?? 0)),
            rangee('Écart à l’effectif retenu', (c) =>
              (c.pm?.ecart ?? 0) > 0 ? `+${entier(c.pm?.ecart ?? 0)}` : entier(c.pm?.ecart ?? 0),
            ),
          ),
        ),
      ),
    );

    // ── Structure comparée ────────────────────────────────────────────────
    const categoriesCharges = categoriesDe('charge');
    zone.appendChild(
      graphiqueEmpile(
        // Une « année » par scénario : l'axe porte les scénarios, pas le temps.
        colonnes.map((_, i) => i + 1),
        categoriesCharges.map((cat, rang) => ({
          nom: cat,
          couleur: teinte(rang),
          valeurs: colonnes.map((c) =>
            premiere.ex.charges
              .filter((l) => l.poste.categorie === cat)
              .reduce((s, p) => s + montant(c, p.poste.code), 0),
          ),
        })),
        { titre: `Structure des charges comparée — ${anneeChoisie}`, enteteAbscisse: 'Rang du scénario' },
      ),
    );
    zone.appendChild(
      legende([
        ...categoriesCharges.map((cat, rang) => ({ nom: cat, couleur: teinte(rang) })),
      ]),
    );
    zone.appendChild(
      noteLecture(
        `Rangs de l'axe : ${colonnes.map((c, i) => `${i + 1} = ${c.libelle}`).join(' · ')}.`,
      ),
    );

    // ── Lecture ───────────────────────────────────────────────────────────
    const ecartReserve = Math.max(...reserves) - Math.min(...reserves);
    zone.appendChild(
      insight(
        pire.ex.resultat < 0 && meilleur.ex.resultat > 0 ? 'warning' : 'info',
        '≡',
        `**Ce que ${anneeChoisie} met en jeu.** Les scénarios s'écartent de ` +
          `${euros(Math.max(...resultats) - Math.min(...resultats))} sur le résultat et de ` +
          `${euros(ecartReserve)} sur la réserve — de ${euros(Math.min(...reserves))} à ` +
          `${euros(Math.max(...reserves))}. ` +
          (pire.ex.resultat < 0 && meilleur.ex.resultat > 0
            ? `L'exercice bascule d'un scénario à l'autre : « ${meilleur.libelle} » dégage ` +
              `${eurosSigne(meilleur.ex.resultat)} quand « ${pire.libelle} » perd ` +
              `${euros(Math.abs(pire.ex.resultat))}. C'est donc l'année où l'hypothèse retenue compte.`
            : `Tous les scénarios vont dans le même sens sur cet exercice.`),
      ),
    );
  };

  // ── Sélecteur d'exercice ────────────────────────────────────────────────
  for (const an of annees) {
    selecteur.appendChild(
      h(
        'button',
        {
          classe: 'btn-action',
          type: 'button',
          'aria-pressed': an === anneeChoisie ? 'true' : 'false',
          surClick: () => {
            anneeChoisie = an;
            exerciceDemande = an;
            for (const b of selecteur.querySelectorAll('button')) {
              b.setAttribute('aria-pressed', b.textContent === String(an) ? 'true' : 'false');
            }
            dessiner();
          },
        },
        String(an),
      ),
    );
  }

  /** Marque le bouton actif dans le sélecteur. */
  const marquer = (): void => {
    for (const b of selecteur.querySelectorAll('button')) {
      b.setAttribute('aria-pressed', b.textContent === String(anneeChoisie) ? 'true' : 'false');
    }
  };

  appliquerDemande = (annee) => {
    if (!annees.includes(annee)) return;
    anneeChoisie = annee;
    marquer();
    dessiner();
  };

  marquer();
  dessiner();

  return h(
    'div',
    { classe: 'page', id: 'page-exercice-compare', role: 'region', 'aria-label': 'Exercice comparé' },
    h(
      'section',
      {},
      enteteSection(
        1,
        'Un exercice, tous les scénarios',
        'Le budget poste par poste, scénario contre scénario',
      ),
      h(
        'p',
        { classe: 'text-block' },
        '**La grille de construction budgétaire montre un scénario sur cinq ans.** Celle-ci fait l’inverse : ' +
          'une année, tous les scénarios, ligne à ligne. C’est la vue de l’arbitrage annuel — la colonne ' +
          '« écart » dit d’un coup d’œil sur quels postes les hypothèses divergent, et lesquels ne changent pas ' +
          'quel que soit le scénario retenu.',
      ),
      h(
        'div',
        { classe: 'barre-actions' },
        h('span', { classe: 'titre' }, 'Exercice'),
        selecteur,
      ),
      zone,
      regleAccent('Poursuivre'),
      h(
        'div',
        { classe: 'barre-actions' },
        h(
          'button',
          { classe: 'btn-action', type: 'button', surClick: () => { allerA('budget'); } },
          'Construction budgétaire — un scénario sur cinq ans',
        ),
        h(
          'button',
          { classe: 'btn-action', type: 'button', surClick: () => { allerA('veille'); } },
          'Veille des réserves',
        ),
        h(
          'button',
          { classe: 'btn-action', type: 'button', surClick: () => { allerA('tresorerie'); } },
          'Trésorerie et BFR',
        ),
      ),
      noteLecture(
        `Un montant suivi d'un point a été arbitré à la main dans ce scénario ; les autres sont engendrés par ` +
          `les inducteurs. L'arbitrage se fait depuis la grille de construction budgétaire, scénario par ` +
          `scénario — les scénarios votés y sont en lecture seule, toute modification créant un scénario ` +
          `dérivé.`,
      ),
    ),
  );
}
